import { FC, useRef, useState } from "react";
import { QESButton } from "@app/shared/qes-standards/Button";
import { AiResponseReviewService } from "@app/operations/ai-response-review/aiResponseReviewService";
import { AiChatMessageType, AiResponseReviewQueryModel, AiResponseReviewResult } from "@app/operations/ai-response-review/interfaces";
import { formatRating } from "@app/operations/ai-response-review/formatRating";
import { formatDateTime } from "@app/shared/formatDateTime";

interface AiResponseReviewExportProps {
  filters: AiResponseReviewQueryModel;
  totalCount: number;
  /**
   * True while `totalCount` still belongs to the previous filters rather than
   * `filters`. Exporting in that window would pair the new filters with the old
   * row count, truncating the CSV if the new filters match more rows.
   */
  isPlaceholderData: boolean;
}

const CSV_COLUMNS: Array<{ header: string; getValue: (item: AiResponseReviewResult) => string | number }> = [
  { getValue: item => item.responseId, header: "Response ID" },
  { getValue: item => formatDateTime(item.createdUtc), header: "Date/Time (UTC)" },
  { getValue: item => item.clientId, header: "Client" },
  { getValue: item => item.clientName, header: "Client Name" },
  { getValue: item => item.projectId, header: "Project" },
  { getValue: item => item.environment, header: "Environment" },
  { getValue: item => item.userId, header: "User" },
  { getValue: item => item.userName, header: "User Name" },
  { getValue: item => item.prompt, header: "Prompt" },
  { getValue: item => item.response, header: "Quinn Response" },
  { getValue: item => item.semanticKernelTemperature ?? "", header: "Temperature" },
  { getValue: item => item.searchServiceScoreCutoff ?? "", header: "Score Cutoff" },
  { getValue: item => item.searchServiceMaximumDefinitions ?? "", header: "Max Definitions" },
  { getValue: item => item.responseTimeMs, header: "Response Time (ms)" },
  { getValue: item => formatRating(item.userRating), header: "User Rating" },
  { getValue: item => item.userReason ?? "-", header: "User Reason" },
  { getValue: item => formatRating(item.internalRating), header: "Internal Rating" },
  { getValue: item => item.internalReason ?? "-", header: "Internal Reason" },
  { getValue: item => item.internalLastUpdatedUtc ? formatDateTime(item.internalLastUpdatedUtc) : "-", header: "Last Updated" },
  { getValue: item => item.messageType === AiChatMessageType.Table ? "Y" : "N", header: "Table Generated" },
  { getValue: item => item.tableDownloaded === true ? "Y" : item.tableDownloaded === false ? "N" : "-", header: "Table Downloaded" },
  { getValue: item => item.previousSqlLoaded === true ? "Y" : item.previousSqlLoaded === false ? "N" : "-", header: "Previous Sql Loaded" }
];

const escapeCsvValue = (value: string | number): string => {
  const stringValue = String(value);

  if (/[",\n\r]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

const buildCsv = (items: AiResponseReviewResult[]): string => {
  const headerRow = CSV_COLUMNS.map(column => escapeCsvValue(column.header)).join(",");
  const rows = items.map(item => CSV_COLUMNS.map(column => escapeCsvValue(column.getValue(item))).join(","));

  return [headerRow, ...rows].join("\r\n");
};

const AiResponseReviewExport: FC<AiResponseReviewExportProps> = ({ filters, totalCount, isPlaceholderData }) => {
  const [isExporting, setIsExporting] = useState(false);
  const service = useRef(new AiResponseReviewService()).current;

  const handleExport = async () => {
    if (totalCount <= 0 || isExporting || isPlaceholderData) {
      return;
    }

    setIsExporting(true);

    try {
      const result = await service.getAiResponseReviews({
        ...filters,
        pageNumber: 1,
        pageSize: totalCount
      });

      const csv = buildCsv(result.items);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.download = `AI_Response_Review_${new Date().toLocaleDateString("en-US").replace(/\//g, "_")}.csv`;
      link.href = url;
      link.click();

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting AI response reviews:", err);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <QESButton
      qesTestId="ai-response-review__download-table-button"
      qesType="secondary"
      onClick={handleExport}
      disabled={isExporting || totalCount <= 0 || isPlaceholderData}
    >
      Download Table
    </QESButton>
  );
};

export default AiResponseReviewExport;
